// components/Accordion.tsx
import React, { useState } from 'react';
import AccordionItem from './accordion-item';
import Head from 'next/head';
import './_style.module.scss';

interface FAQ {
    id: number;
    header: string;
    text: React.ReactNode;
    /** Plain answer for FAQ JSON-LD when `text` is rich HTML / React nodes */
    answerPlain?: string;
}

interface AccordionProps {
    faqs: FAQ[];
    showGroups?: boolean;
    listKey?: number;
    defaultActiveId?: number | null;
}

const Accordion: React.FC<AccordionProps> = ({ faqs, showGroups, listKey, defaultActiveId = null }) => {
    const [activeId, setActiveId] = useState<number | null>(defaultActiveId);
    const handleToggle = (id: number) => {
        setActiveId(activeId === id ? null : id);
    };
    if (listKey == undefined){
        listKey = 3
    }

    // Ensure faqs is always an array
    const faqsArray = Array.isArray(faqs) ? faqs : []

    // Generate FAQ JSON-LD schema
    const generateFAQSchema = () => {
        if (!faqsArray || faqsArray.length === 0) return null;

        const mainEntity = faqsArray.map((faq) => {
            let answerText = '';

            if (faq.answerPlain) {
                answerText = faq.answerPlain;
            } else if (typeof faq.text === 'string') {
                answerText = faq.text;
            } else if (React.isValidElement(faq.text)) {
                const props = faq.text.props as { children?: unknown; dangerouslySetInnerHTML?: { __html: string } };
                if (props.dangerouslySetInnerHTML?.__html) {
                    // Strip HTML tags to get plain text for schema
                    answerText = props.dangerouslySetInnerHTML.__html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                } else if ('children' in props) {
                    if (typeof props.children === 'string') {
                        answerText = props.children;
                    } else if (Array.isArray(props.children)) {
                        answerText = (props.children as (string | unknown)[])
                            .map((child: any) => typeof child === 'string' ? child : '')
                            .join(' ');
                    }
                }
            } else {
                answerText = String(faq.text);
            }

            return {
                "@type": "Question",
                "name": faq.header,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": answerText
                }
            };
        });

        return {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": mainEntity
        };
    };

    const faqSchema = generateFAQSchema();

    if (showGroups) {
        return (
            <>
                {faqSchema && (
                    <Head>
                        <script
                            type="application/ld+json"
                            dangerouslySetInnerHTML={{
                                __html: JSON.stringify(faqSchema)
                            }}
                        />
                    </Head>
                )}
                <div className="accordion">
                    <div className="accordion-group">
                        {faqsArray.map((faq) => (
                            <AccordionItem
                                key={faq.id}
                                id={faq.id}
                                header={faq.header}
                                text={faq.text}
                                activeId={activeId}
                                handleToggle={handleToggle}
                            />
                        ))}
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {faqSchema && (
                <Head>
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(faqSchema)
                        }}
                    />
                </Head>
            )}
            <div className="accordion">
                {faqsArray.map((faq) => (
                    <AccordionItem
                        key={faq.id}
                        id={faq.id}
                        header={faq.header}
                        text={faq.text}
                        activeId={activeId}
                        handleToggle={handleToggle}
                    />
                ))}
            </div>
        </>
    );
};

export default Accordion;
